import { Box, Button, Card, CardMedia, Grid, Typography } from '@mui/material';
import React, { useEffect } from 'react';
import { VerificationProcessService } from '../../../../Component/Api/VerificationAgency/VerificationProcess/VerificationProcessService';
import { SmallLoading } from '../../../../Component/Common/Components/Loading/IntermediateLoading';
import { VerificationProcess } from '../../../../Component/Model/Dto/VerificationAgency/VerificationProcessTracker';
import { VerificationProcessStatus } from '../../../../Component/Model/Enum/BEE123Enums';
import { getVerificationProcessImage, getVerificationStatusColour } from '../../../../Component/Model/Enum/VerificationProcess';
import { getDateAsString } from '../../../../Helpers/DateFormatter';

import AttachmentIcon from '@mui/icons-material/Attachment';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import PanoramaFishEyeIcon from '@mui/icons-material/PanoramaFishEye';
import StickyNote2Icon from '@mui/icons-material/StickyNote2';
import { NoDataScreen } from '../../../../Component/Common/Components/NoDataScreen/NoDataScreen';
import { isNullOrEmpty, removeHtml } from '../../../../Component/Model/helpers';
import colors from '../../../../assets/theme/base/colors';
import { Attachments } from '../../../Common/Attachments/Attachments';
import { PlannerNoteModal } from './PlannerNoteModal';

const { secondary } = colors;

interface IProps {
    scorecardId: string;
    status?: VerificationProcessStatus;
}

export const PlannerCards = (props: IProps) => {

    const [verificationProcessData, setVerificationProcessData] = React.useState<VerificationProcess.VerificationProcessDto[]>([]);
    const [isLoading, setIsLoading] = React.useState(false);
    const [showAttachmentsModal, setShowAttachmentsModal] = React.useState(false);
    const [showNoteModal, setShowNoteModal] = React.useState(false);
    const [selectedItem, setSelectedItem] = React.useState<VerificationProcess.VerificationProcessDto | null>(null);

    const fetchData = async () => {
        setIsLoading(true);
        try {
            const response = await VerificationProcessService.GetScorecardVerificationProcesses(props.scorecardId, props.status);
            setVerificationProcessData(response.data);
        } catch (error) {
            console.log(error);
        } finally {
            setIsLoading(false);
        }
    }

    const getVerificationStatusIcon = (status: VerificationProcessStatus) => {
        switch (status) {
            case VerificationProcessStatus.Planned:
                return <PanoramaFishEyeIcon />;
            case VerificationProcessStatus['In Progress']:
                return <HourglassBottomIcon />;
            case VerificationProcessStatus.Completed:
                return <CheckCircleOutlineIcon />;
            default:
                return null;
        }
    }

    const showAttachmentsModalForSelectedItem = (item: VerificationProcess.VerificationProcessDto) => {
        setSelectedItem(item);
        setShowAttachmentsModal(true);
    }

    const closeAttachmentsModalForSelectedItem = () => {
        setSelectedItem(null);
        setShowAttachmentsModal(false);
    }

    const showNoteModalForSelectedItem = (item: VerificationProcess.VerificationProcessDto) => {
        setSelectedItem(item);
        setShowNoteModal(true);
    }

    const closeNoteModalForSelectedItem = () => {
        setSelectedItem(null);
        setShowNoteModal(false);
    }

    useEffect(() => {
        fetchData();
    }, [props.scorecardId, props.status]);


    if (isLoading) {
        return (
            <Grid container justifyContent="center" height="50vh">
                <SmallLoading />
            </Grid>
        );
    }

    return (
        <>
            {verificationProcessData.length === 0 &&
                <NoDataScreen
                    subText1='You do not have any items in this section.'
                    subText2='Feel free to reach out to your verification agency for more information.' />
            }
            {verificationProcessData.length > 0 &&
                <Grid container spacing={3}>
                    {verificationProcessData.map((item, index) => {
                        const image = getVerificationProcessImage(item.image);
                        const imageUrl = `${process.env.PUBLIC_URL}/img/verificationProcess/${image}`;
                        const colour = getVerificationStatusColour(item.verificationStatus);
                        return (
                            <Grid key={`Verification-Process-${index}`} item>
                                <Box
                                    sx={{
                                        width: '360px',
                                        height: '340px',
                                        display: 'flex',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <Card
                                        sx={{
                                            border: `3px solid ${colour}`,
                                            padding: 0,
                                            width: '100%'
                                        }}
                                    >
                                        <CardMedia
                                            sx={{ height: '200px', margin: 0, borderRadius: '0.5rem 0.5rem 0 0' }}
                                            image={imageUrl}
                                            title={image}
                                        />
                                        <Box sx={{ margin: 0, padding: 0 }}>
                                            <Box
                                                sx={{
                                                    backgroundColor: `${colour}`,
                                                    color: '#fff',
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    padding: 1
                                                }}
                                            >
                                                <Typography variant='body1' style={{ display: 'flex', alignItems: 'center', fontSize: "medium" }}>
                                                    {getVerificationStatusIcon(item.verificationStatus)}
                                                    <span style={{ marginLeft: '8px' }}>
                                                        {VerificationProcessStatus[item.verificationStatus]}
                                                    </span>
                                                </Typography>

                                                <Typography variant='body1' fontSize="medium">
                                                    {getDateAsString(item.scheduledDate)}
                                                </Typography>
                                            </Box>

                                            <Box sx={{
                                                margin: 1,
                                                marginBottom: 0
                                            }}>
                                                <Typography variant="h5">
                                                    {item.typeDescription}
                                                </Typography>
                                                <Box sx={{
                                                    borderBottom: 1,
                                                    borderColor: 'divider',
                                                    margin: 0.7
                                                }} />
                                                <Box sx={{
                                                    display: 'flex'
                                                }}>
                                                    {!isNullOrEmpty(removeHtml(item.note)) && (
                                                        <Button variant="text" sx={{ padding: 1 }} style={{ color: secondary.main }} startIcon={<StickyNote2Icon />}
                                                            onClick={() => showNoteModalForSelectedItem(item)}>
                                                            View Note
                                                        </Button>
                                                    )}
                                                    {item.documentCount > 0 && (
                                                        <Button variant="text" sx={{ padding: 1 }} style={{ color: secondary.main }} startIcon={<AttachmentIcon />}
                                                            onClick={() => showAttachmentsModalForSelectedItem(item)}>
                                                            View Attachments
                                                        </Button>
                                                    )}
                                                </Box>
                                            </Box>
                                        </Box>
                                    </Card>
                                </Box>
                            </Grid>
                        )
                    })}
                </Grid>
            }

            {selectedItem && showAttachmentsModal &&
                <Attachments
                    scorecardId={props.scorecardId}
                    link='VerificationProcessRow'
                    itemId={selectedItem.id}
                    showModal={showAttachmentsModal}
                    title={`View attachments for ${selectedItem.typeDescription}`}
                    handleClose={closeAttachmentsModalForSelectedItem}
                />
            }

            {
                selectedItem && showNoteModal &&
                <PlannerNoteModal
                    showModal={showNoteModal}
                    title={`View note for ${selectedItem.typeDescription}`}
                    text={selectedItem.note}
                    handleClose={closeNoteModalForSelectedItem}
                />
            }
        </>
    )
}
